<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  initialTime: {
    type: Date,
    default: null,
  },
  requireReason: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'chooseTime']);
const { locale, t } = useI18n();
const snoozeTime = ref(props.initialTime);
const reason = ref('');

watch(
  () => props.initialTime,
  value => {
    snoozeTime.value = value;
  }
);

const datePickerLanguage = computed(() =>
  locale.value.toLowerCase().startsWith('zh') ? 'zh-cn' : 'en'
);

const canSubmit = computed(
  () =>
    snoozeTime.value &&
    (!props.requireReason || reason.value.trim().length > 0)
);

const chooseTime = () => {
  if (!canSubmit.value) return;

  emit('chooseTime', snoozeTime.value, reason.value.trim());
};

const disabledDate = date => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date < yesterday;
};

const disabledTime = date => {
  const minimumTime = new Date();
  minimumTime.setHours(minimumTime.getHours() + 1);
  return date < minimumTime;
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header
      :header-title="
        requireReason
          ? t('CONVERSATION.CUSTOM_SNOOZE.FOLLOW_UP_TITLE')
          : t('CONVERSATION.CUSTOM_SNOOZE.TITLE')
      "
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6"
      @submit.prevent="chooseTime"
    >
      <label
        v-if="requireReason"
        class="mb-3 block text-sm font-medium text-n-slate-12"
      >
        {{ t('CONVERSATION.CUSTOM_SNOOZE.REASON_LABEL') }}
        <textarea
          v-model="reason"
          required
          maxlength="500"
          rows="3"
          class="mt-2 w-full resize-none rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('CONVERSATION.CUSTOM_SNOOZE.REASON_PLACEHOLDER')"
        />
      </label>
      <div
        v-if="requireReason"
        class="mb-2 text-sm font-medium text-n-slate-12"
      >
        {{ t('CONVERSATION.CUSTOM_SNOOZE.DUE_AT_LABEL') }}
      </div>
      <DatePicker
        v-model:value="snoozeTime"
        type="datetime"
        inline
        input-class="mx-input"
        :lang="datePickerLanguage"
        :disabled-date="disabledDate"
        :disabled-time="disabledTime"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('CONVERSATION.CUSTOM_SNOOZE.CANCEL')"
          @click.prevent="emit('close')"
        />
        <NextButton
          type="submit"
          :disabled="!canSubmit"
          :label="
            requireReason
              ? t('CONVERSATION.CUSTOM_SNOOZE.CREATE_FOLLOW_UP')
              : t('CONVERSATION.CUSTOM_SNOOZE.APPLY')
          "
        />
      </div>
    </form>
  </div>
</template>
